import { useState, useRef, useEffect } from "react";
import type { Product } from "@/types/product";
import { wineCategories, countries, countryGroups, grapes, grapeGroups } from "@/mocks/products";
import { uploadImage } from "@/lib/upload";

interface ProductModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onSave: (product: Product) => void;
}

const emptyProduct: Product = {
  id: "",
  brand: "",
  name: "",
  vintage: "",
  country: "",
  region: "",
  grape: "",
  costPrice: 0,
  sellingPrice: 0,
  stock: 0,
  safetyStock: 0,
  status: "active",
  description: "",
  image: null,
  category: "紅酒",
  createdAt: new Date().toISOString(),
};

export default function ProductModal({ isOpen, product, onClose, onSave }: ProductModalProps) {
  const [form, setForm] = useState<Product>(emptyProduct);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!product;

  useEffect(() => {
    if (isOpen) {
      if (product) {
        setForm(product);
        setPreviewImage(product.image);
      } else {
        setForm(emptyProduct);
        setPreviewImage(null);
      }
      setErrors({});
    }
  }, [isOpen, product]);

  if (!isOpen) return null;

  const handleChange = (field: keyof Product, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: "圖片大小不能超過 5MB" }));
      return;
    }
    try {
      // multipart 上傳到本地後端，回傳 URL（取代 base64）
      const url = await uploadImage(file);
      setPreviewImage(url);
      setForm((prev) => ({ ...prev, image: url }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        image: err instanceof Error ? err.message : "圖片上傳失敗",
      }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.brand.trim()) newErrors.brand = "請輸入品牌/酒莊";
    if (!form.name.trim()) newErrors.name = "請輸入酒款名稱";
    if (!form.vintage.trim()) newErrors.vintage = "請輸入年份";
    if (!form.country) newErrors.country = "請選擇國家";
    if (!form.region.trim()) newErrors.region = "請輸入產區";
    if (!form.grape) newErrors.grape = "請選擇品種";
    if (form.sellingPrice <= 0) newErrors.sellingPrice = "售價必須大於 0";
    if (form.stock < 0) newErrors.stock = "庫存不能為負數";
    if (form.safetyStock < 0) newErrors.safetyStock = "安全庫存不能為負數";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const productToSave: Product = {
      ...form,
      id: isEditing ? form.id : `prod-${Date.now()}`,
    };
    onSave(productToSave);
    onClose();
  };

  const inputClass = (field: string) =>
    `w-full px-3 py-2 rounded-lg border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors ${errors[field] ? "border-accent-400" : "border-background-200"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-[720px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 sticky top-0 bg-background-50 z-10">
          <div>
            <h2 className="text-base font-bold text-foreground-900">
              {isEditing ? "編輯商品" : "新增商品"}
            </h2>
            {!isEditing && (
              <p className="text-xs text-foreground-400 mt-0.5">
                新增後將存入商品資料庫，所有帳號皆可檢視與使用。
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-5">
          {/* Image Upload */}
          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-2">
              商品圖片（縮圖）
            </label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-background-200 bg-background-100 overflow-hidden shrink-0">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="preview"
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-foreground-300 text-xl" />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <i className="ri-upload-cloud-2-line" />
                  上傳圖片
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                {errors.image && (
                  <p className="text-xs text-accent-500">{errors.image}</p>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              基本資料
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-foreground-500 block mb-1">品牌 / 酒莊</label>
                <input
                  type="text"
                  value={form.brand}
                  onChange={(e) => handleChange("brand", e.target.value)}
                  placeholder="例：Chateau Margaux"
                  className={inputClass("brand")}
                />
                {errors.brand && <p className="text-xs text-accent-500 mt-1">{errors.brand}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">酒款名稱</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="例：Grand Vin"
                  className={inputClass("name")}
                />
                {errors.name && <p className="text-xs text-accent-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">年份</label>
                <input
                  type="text"
                  value={form.vintage}
                  onChange={(e) => handleChange("vintage", e.target.value)}
                  placeholder="例：2020"
                  className={inputClass("vintage")}
                />
                {errors.vintage && <p className="text-xs text-accent-500 mt-1">{errors.vintage}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">國家</label>
                <select
                  value={form.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  className={inputClass("country")}
                >
                  <option value="">請選擇國家</option>
                  {form.country && !countries.includes(form.country) && (
                    <option value={form.country}>{form.country}</option>
                  )}
                  {countryGroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors.country && <p className="text-xs text-accent-500 mt-1">{errors.country}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">產區</label>
                <input
                  type="text"
                  value={form.region}
                  onChange={(e) => handleChange("region", e.target.value)}
                  placeholder="例：Pauillac"
                  className={inputClass("region")}
                />
                {errors.region && <p className="text-xs text-accent-500 mt-1">{errors.region}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">品種</label>
                <select
                  value={form.grape}
                  onChange={(e) => handleChange("grape", e.target.value)}
                  className={inputClass("grape")}
                >
                  <option value="">請選擇品種</option>
                  {form.grape && !grapes.includes(form.grape) && (
                    <option value={form.grape}>{form.grape}</option>
                  )}
                  {grapeGroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors.grape && <p className="text-xs text-accent-500 mt-1">{errors.grape}</p>}
              </div>
            </div>
          </div>

          {/* Price & Stock */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              價格與庫存
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-foreground-500 block mb-1">成本 NT$</label>
                <input
                  type="number"
                  value={form.costPrice || ""}
                  onChange={(e) => handleChange("costPrice", Number(e.target.value))}
                  placeholder="留空則自動以售價 65% 計算"
                  className={inputClass("costPrice")}
                />
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">售價 NT$</label>
                <input
                  type="number"
                  value={form.sellingPrice || ""}
                  onChange={(e) => handleChange("sellingPrice", Number(e.target.value))}
                  placeholder="例：28000"
                  className={inputClass("sellingPrice")}
                />
                {errors.sellingPrice && <p className="text-xs text-accent-500 mt-1">{errors.sellingPrice}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">庫存（瓶）</label>
                <input
                  type="number"
                  value={form.stock || ""}
                  onChange={(e) => handleChange("stock", Number(e.target.value))}
                  placeholder="例：10"
                  className={inputClass("stock")}
                />
                {errors.stock && <p className="text-xs text-accent-500 mt-1">{errors.stock}</p>}
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">安全庫存（瓶）</label>
                <input
                  type="number"
                  value={form.safetyStock || ""}
                  onChange={(e) => handleChange("safetyStock", Number(e.target.value))}
                  placeholder="例：12"
                  className={inputClass("safetyStock")}
                />
                {errors.safetyStock && <p className="text-xs text-accent-500 mt-1">{errors.safetyStock}</p>}
              </div>
            </div>
          </div>

          {/* Category & Status */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              商品狀態
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-foreground-500 block mb-1">分類</label>
                <div className="flex flex-wrap gap-2">
                  {wineCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => handleChange("category", cat)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                        form.category === cat
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              介紹
            </h3>
            <div>
              <label className="text-xs text-foreground-500 block mb-1">描述介紹</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="可手動輸入酒款風味、適飲場景、搭配推薦等，留空時建立後將由系統自動產生介紹"
                maxLength={500}
                rows={4}
                className={`${inputClass("description")} resize-none`}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-foreground-400">{form.description.length}/500</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-background-100 sticky bottom-0 bg-background-50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-colors"
          >
            {isEditing ? "儲存" : "建立"}
          </button>
        </div>
      </div>
    </div>
  );
}